/**
 * GL8610XL Manual Journal Entry Transformer
 *
 * Converts a simple JE spreadsheet into GL8610XL keyword-based format.
 *
 * Source expectations:
 *   Columns: unit, account, debit, credit, description/remarks
 *   Optional: company_id, accounting_date, entry_type, project_type, project_id
 *
 * Output format (GL8610XL):
 *   Company ID | <value>
 *   Accounting Date | <YYYYMMDD>
 *   Entry Type | JE
 *   Remarks | <value>
 *   Layout | Unit | Account | Amount | Project Type | Project Id | Remarks
 *   Detail | <unit> | <acct> | <amount> | ...
 *
 * Rules:
 *   - Debits are positive amounts, credits are negative
 *   - Each JE batch must balance to zero
 *   - A new JE is created when company or accounting date changes
 */
import * as XLSX from 'xlsx';
import { extractKeywordMeta, formatAccountingDate } from '../lib/isolved-parser';
import { resolveLineAmount, partitionZeroLines } from '../lib/amounts';

export function transformJE(data, sourceWb) {
  const meta = detectMeta(data, sourceWb);
  const wb = XLSX.utils.book_new();
  const sheetData = [];

  // Drop $0 lines up front so the output rows and the preview rows below come
  // from the same set — MV doesn't need them and they can't affect the balance.
  const { kept, zeroCount } = partitionZeroLines(data);
  meta.droppedZeroLines = zeroCount;

  // Group rows into journal entries (by company + date if available)
  const entries = groupIntoEntries(kept, meta);

  for (const entry of entries) {
    // Header keywords
    sheetData.push(['Company ID', entry.companyId]);
    sheetData.push(['Accounting Date', entry.accountingDate]);
    sheetData.push(['Entry Type', entry.entryType || 'JE']);
    if (entry.entryStatus) {
      sheetData.push(['Entry Status', entry.entryStatus]);
    }
    if (entry.remarks) {
      sheetData.push(['Remarks', entry.remarks]);
    }

    // Layout row
    const layoutCols = ['Unit', 'Account', 'Amount'];
    if (entry.hasProjects) {
      layoutCols.push('Project Type', 'Project Id');
    }
    layoutCols.push('Remarks');
    sheetData.push(['Layout', ...layoutCols]);

    // Detail rows
    entry.rows.forEach((row) => {
      const unit = String(row.unit ?? row.unit_id ?? '').trim().padStart(3, '0');
      const acct = String(row.account ?? row.account_no ?? '').trim();
      // null (no usable amount) is written as 0; the validator errors on it, so
      // the file can't be downloaded until the source is fixed.
      const amount = resolveLineAmount(row) ?? 0;
      const remarks = String(row.description ?? row.remarks ?? '').trim();

      const detailRow = ['Detail', unit, acct, amount];
      if (entry.hasProjects) {
        detailRow.push(
          String(row.project_type ?? '').trim(),
          String(row.project_id ?? row.project ?? '').trim()
        );
      }
      detailRow.push(remarks);
      sheetData.push(detailRow);
    });

    sheetData.push([]); // blank separator between entries
  }

  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  XLSX.utils.book_append_sheet(wb, ws, 'JournalEntries');

  // Preview rows for validation — same filtered set and same resolveLineAmount()
  // as the real output above, so the two can't diverge.
  const previewRows = kept.map((row) => {
    const unit = String(row.unit ?? row.unit_id ?? '').trim().padStart(3, '0');
    const acct = String(row.account ?? row.account_no ?? '').trim();
    const debit = parseFloat(row.debit ?? 0) || 0;
    const credit = parseFloat(row.credit ?? 0) || 0;
    return { unit, account: acct, debit, credit, amount: resolveLineAmount(row) };
  });

  return { workbook: wb, rows: previewRows, meta };
}

function detectMeta(data, sourceWb) {
  // `data` is already correctly parsed by ingest.js's buildPlan/applyPlan, which
  // locates the real header row even when title/blank rows precede it. Scanning
  // sourceWb's raw rows again here (assuming row 0 is the header) misreads any
  // leading junk row and can even misread the tabular header row itself as
  // keyword/value pairs — so canonical `data` is the primary source, and the
  // keyword scan (for genuine keyword-style files, e.g. Company ID / Accounting
  // Date rows in column A) is only a fallback for fields data doesn't have.
  const keywordMeta = extractKeywordMeta(sourceWb, {
    accountingDate: '',
    entryStatus: '',
    remarks: '',
  });

  const first =
    data.find((r) => r.company_id || r.accounting_date || r.entry_type) || data[0] || {};

  const companyId = String(first.company_id ?? first.company ?? keywordMeta.companyId ?? '').trim();

  let accountingDate = first.accounting_date ? formatAccountingDate(first.accounting_date) : '';
  if (!accountingDate) accountingDate = keywordMeta.accountingDate;
  if (!accountingDate) accountingDate = formatDate(new Date());

  const entryType =
    (first.entry_type && String(first.entry_type).toUpperCase().trim()) ||
    keywordMeta.entryType ||
    'JE';

  return {
    companyId,
    accountingDate,
    entryType,
    entryStatus: keywordMeta.entryStatus,
    remarks: keywordMeta.remarks,
  };
}

function groupIntoEntries(data, meta) {
  // Group by company_id + accounting_date if present, otherwise one big entry
  const groups = {};
  data.forEach((row) => {
    const cid = String((row.company_id ?? row.company ?? meta.companyId) || '01').trim();
    const rawDate = row.accounting_date ?? meta.accountingDate;
    const date = formatAccountingDate(rawDate) || String(rawDate).trim();
    const key = `${cid}|${date}`;
    if (!groups[key]) {
      groups[key] = {
        companyId: cid,
        accountingDate: date,
        entryType: String((row.entry_type ?? meta.entryType) || 'JE').trim(),
        entryStatus: String(row.entry_status ?? meta.entryStatus ?? '').trim(),
        remarks: String(row.je_remarks ?? row.header_remarks ?? meta.remarks ?? '').trim(),
        hasProjects: false,
        rows: [],
      };
    }
    if (row.project_type || row.project_id || row.project) {
      groups[key].hasProjects = true;
    }
    groups[key].rows.push(row);
  });

  return Object.values(groups);
}

function formatDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}
